import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { always, head, map, times } from 'ramda'
import { Box } from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import { getImageUrl } from '@/utils/image'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import LoginRequiredCard from '@/features/ListingDetail/login-required-card'
import TermsRequiredCard from '@/features/ListingDetail/terms-required-card'
import useTourLinks from '@/features/ListingDetail/use-tour-links'
import { Cube, PlayButtonIcon } from '@/components/icons'
import { Lightbox, useLightbox } from '@/components/image-lightbox'
import { PhotoGrid } from '@/components/photo-grid'
import { PhotoSlider } from '@/components/photo-slider'

import PhotoSliderBlurred from '../photo-slider/photo-slider-blurred'

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
`

const MobileButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 12px;
  align-items: center;
  justify-content: left;
  gap: 8px;
  position: absolute;
  bottom: 10%;
`

const StyledButton = styled.button`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.5);
  position: relative;

  color: ${colors.white};
  text-align: center;

  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
`

const getLightboxPhotos = (unitPhotos, slider) => {
  const photoConfig = {
    fit: 'fit-in/1150x785',
    quality: 100
  }
  const photos = map(
    ({ aws_s3_key }) => getImageUrl(aws_s3_key, photoConfig),
    unitPhotos
  )
  return slider && photos.length === 1 ? times(always(head(photos)), 3) : photos
}

const ListingPhotos = ({
  images,
  sliderSettings,
  altText,
  buttonText,
  listingLinks = {},
  unitVideos = {},
  isListingLocked = false,
  listings
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const useSlider = displayMobile
  const largeImages = getLightboxPhotos(images, useSlider)
  const { props, openImage, onMouseDown, reset } = useLightbox(largeImages)
  const rawImages = images.map(({ aws_s3_key }) => aws_s3_key)
  const { handleVideoClick, handle3DTourClick, showVideoLink, show3dTourLink } =
    useTourLinks({
      listingLinks,
      unitVideos
    })

  const isLoggedIn = useSessionComplete()
  const CardComponent = isLoggedIn ? TermsRequiredCard : LoginRequiredCard

  return (
    <div className={useSlider ? 'photo-carousel' : 'photo-grid'}>
      {useSlider ? (
        <ContentWrapper>
          {isListingLocked ? (
            <Box height="full">
              <Box
                position="absolute"
                style={{
                  width: '360px',
                  zIndex: 2,
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {listings && (
                  <CardComponent externalInfo={listings.external_info} />
                )}
              </Box>
              <PhotoSliderBlurred images={rawImages} />
            </Box>
          ) : (
            <>
              <PhotoSlider
                images={rawImages}
                largeImages={largeImages}
                {...sliderSettings}
                altText={altText}
                buttonText={buttonText}
                openImage={openImage}
                onMouseDown={onMouseDown}
              />
              {(showVideoLink || show3dTourLink) && (
                <MobileButtonWrapper>
                  {showVideoLink && (
                    <StyledButton onClick={handleVideoClick}>
                      <PlayButtonIcon width={12} height={12} />
                      {t('common.watch_video', 'Watch Video')}
                    </StyledButton>
                  )}
                  {show3dTourLink && (
                    <StyledButton onClick={handle3DTourClick}>
                      <Cube width={12} height={12} />
                      {t('common.3d_tour', '3D Tour')}
                    </StyledButton>
                  )}
                </MobileButtonWrapper>
              )}
            </>
          )}
        </ContentWrapper>
      ) : isListingLocked ? (
        <Box
          position="absolute"
          style={{
            width: '360px',
            zIndex: 52,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          {listings && <CardComponent externalInfo={listings.external_info} />}
        </Box>
      ) : null}
      <PhotoGrid
        images={rawImages}
        largeImages={largeImages}
        openImage={openImage}
        onMouseDown={reset}
        blurred={isListingLocked}
      />
      <Lightbox {...props} />
    </div>
  )
}

export default ListingPhotos
